import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Building2, Plus, Edit2, Trash2, MapPin } from 'lucide-react';
import Modal from './Modal';
import { Input } from './Form';

interface Branch {
  id: string;
  name: string;
  isHeadquarters: boolean;
  document?: string | null;
  stateRegistration?: string | null;
  creci?: string | null;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  zipCode?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
}

const EMPTY: Partial<Branch> = { name: '', country: 'Brasil' };

export default function BranchesManager() {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Partial<Branch> | null>(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/company/branches');
      setBranches(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  function set<K extends keyof Branch>(k: K, v: any) {
    setEditing((p) => ({ ...(p || {}), [k]: v }));
  }

  async function save() {
    if (!editing?.name?.trim()) {
      alert('Informe o nome.');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.patch(`/company/branches/${editing.id}`, editing);
      } else {
        await api.post('/company/branches', editing);
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(b: Branch) {
    if (!confirm(`Excluir a filial "${b.name}"?`)) return;
    try {
      await api.delete(`/company/branches/${b.id}`);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao excluir.');
    }
  }

  return (
    <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="font-semibold dark:text-dark-text-primary flex items-center gap-2">
          <Building2 className="w-5 h-5" /> Imobiliária e Filiais
        </p>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg"
        >
          <Plus className="w-4 h-4" /> Adicionar filial
        </button>
      </div>

      {loading && <p className="text-sm text-gray-400">Carregando...</p>}
      <div className="divide-y divide-gray-100 dark:divide-dark-border">
        {branches.map((b) => (
          <div key={b.id} className="flex items-start justify-between py-3">
            <div>
              <div className="font-medium dark:text-dark-text-primary flex items-center gap-2">
                {b.name}
                {b.isHeadquarters && (
                  <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">Matriz</span>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-dark-text-secondary flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" />
                {[b.city, b.state, b.country].filter(Boolean).join(' / ') || 'Sem endereço'}
                {b.document ? ` · CNPJ ${b.document}` : ''}
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEditing(b)} className="p-2 text-gray-500 hover:text-primary-600">
                <Edit2 className="w-4 h-4" />
              </button>
              {!b.isHeadquarters && (
                <button onClick={() => remove(b)} className="p-2 text-gray-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal
          isOpen
          onClose={() => setEditing(null)}
          title={editing.id ? (editing.isHeadquarters ? 'Editar matriz' : 'Editar filial') : 'Nova filial'}
          size="2xl"
        >
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Nome / Razão social" value={editing.name || ''} onChange={(e) => set('name', e.target.value)} />
              <Input label="CNPJ" value={editing.document || ''} onChange={(e) => set('document', e.target.value)} />
              <Input label="Inscrição Estadual" value={editing.stateRegistration || ''} onChange={(e) => set('stateRegistration', e.target.value)} />
              <Input label="CRECI" value={editing.creci || ''} onChange={(e) => set('creci', e.target.value)} />
              <Input label="E-mail" value={editing.email || ''} onChange={(e) => set('email', e.target.value)} />
              <Input label="Telefone" value={editing.phone || ''} onChange={(e) => set('phone', e.target.value)} />
              <Input label="WhatsApp" value={editing.whatsapp || ''} onChange={(e) => set('whatsapp', e.target.value)} />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Input label="CEP" value={editing.zipCode || ''} onChange={(e) => set('zipCode', e.target.value)} />
              <div className="md:col-span-2">
                <Input label="Logradouro" value={editing.street || ''} onChange={(e) => set('street', e.target.value)} />
              </div>
              <Input label="Número" value={editing.number || ''} onChange={(e) => set('number', e.target.value)} />
              <Input label="Complemento" value={editing.complement || ''} onChange={(e) => set('complement', e.target.value)} />
              <Input label="Bairro" value={editing.neighborhood || ''} onChange={(e) => set('neighborhood', e.target.value)} />
              <Input label="Município (cidade)" value={editing.city || ''} onChange={(e) => set('city', e.target.value)} />
              <Input label="Estado (UF)" value={editing.state || ''} onChange={(e) => set('state', e.target.value)} />
              <Input label="País" value={editing.country || 'Brasil'} onChange={(e) => set('country', e.target.value)} />
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
